import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Save } from "lucide-react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { toast as toaster } from "sonner";

import { BasicInfoForm } from "./basic-info-form";
import { MilkProperties } from "./Milk-Properties";
import { PhysicalAttributesForm } from "./physical-attributes-form";
import { GeneticInfoForm } from "./Genetic-info-form";
import { ParentalInfoForm } from "./Parental-info-form";
import { GrandParentalInfoForm } from "./grandparental-info-form";
import { GreatGrandparentalInfoForm } from "./greate-grandparental-info-form.tsx";
import { StatusInfoForm } from "./status-info-form";
import { FormSummary } from "./form-summary";

import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formSchema, type FormValues } from "@/models/Sila.Animals.schema.ts";
import { useIsMobile } from "@/hooks/use-mobile";
import { registeringAdminAnimal } from "@/features/Admin/Admin.api.ts";
import { toast } from "@/hooks/use-toast.ts";
import { GeneticBreedingValuesForm } from "@/features/Animals/Gneomic-Value.tsx";
import { BodyMeasurementsForm } from "@/features/Animals/Body Measurement.tsx";
import { VideoItem } from "@/components/video-input-manager.tsx";
import { AnimalMediaInfo } from "@/features/Animals/Animal-Media-Info.tsx";
import { useNavigate } from "react-router-dom";
import SequentialUploadNotifier from "@/components/ui/sequential-upload-notifier.tsx";

export function AnimatedMultiStepForm() {
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [currentStep, setCurrentStep] = useState(0);

  type UploadRefType = {
    startUploads: () => Promise<{
      animalImageUrls: string[];
      lineageImageUrls: Record<string, string>;
    }>;
  };

  const [progress, setProgress] = useState(0);
  // @ts-ignore
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>(
    {},
  ); // Track upload progress for each image type
  const uploadRef = useRef<UploadRefType>(null);

  const [animationDirection, setAnimationDirection] = useState<
    "next" | "prev" | null
  >(null);
  const [isAnimating, setIsAnimating] = useState(false);
  // @ts-ignore
  const [imgUrls, setImgUrls] = useState<string[] | unknown[]>([]);

  const [damImageFile, setDamImageFile] = useState<File | string | undefined>();
  const [sireImageFile, setSireImageFile] = useState<
    File | string | undefined
  >();
  // New states for grandparents
  const [paternalGrandfatherImageFile, setPaternalGrandfatherImageFile] =
    useState<File>();
  const [paternalGrandmotherImageFile, setPaternalGrandmotherImageFile] =
    useState<File>();
  const [maternalGrandfatherImageFile, setMaternalGrandfatherImageFile] =
    useState<File>();
  const [maternalGrandmotherImageFile, setMaternalGrandmotherImageFile] =
    useState<File>();

  // Great-grandparent image states (8 total)
  const [
    paternalGrandSireGrandfatherImageFile,
    setPaternalGrandSireGrandfatherImageFile,
  ] = useState<File>();
  const [
    paternalGrandSireGrandmotherImageFile,
    setPaternalGrandSireGrandmotherImageFile,
  ] = useState<File>();
  const [
    paternalGrandDamGrandfatherImageFile,
    setPaternalGrandDamGrandfatherImageFile,
  ] = useState<File>();
  const [
    paternalGrandDamGrandmotherImageFile,
    setPaternalGrandDamGrandmotherImageFile,
  ] = useState<File>();
  const [
    maternalGrandSireGrandfatherImageFile,
    setMaternalGrandSireGrandfatherImageFile,
  ] = useState<File>();
  const [
    maternalGrandSireGrandmotherImageFile,
    setMaternalGrandSireGrandmotherImageFile,
  ] = useState<File>();
  const [
    maternalGrandDamGrandfatherImageFile,
    setMaternalGrandDamGrandfatherImageFile,
  ] = useState<File>();
  const [
    maternalGrandDamGrandmotherImageFile,
    setMaternalGrandDamGrandmotherImageFile,
  ] = useState<File>();

  const formRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const FORM_STORAGE_KEY = "animalFormData";

  // const storage = getStorage(app);
  // const metadata = {
  // 	contentType: "image/jpeg",
  // }

  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation({
    mutationKey: ["adminAnimalRegister"],
    mutationFn: async (data: FormValues) => {
      try {
        await registeringAdminAnimal(data);
      } catch (error) {
        throw error;
      }
    },
    onMutate: async (data: FormValues) => {
      await queryClient.cancelQueries({ queryKey: ["adminAnimalRegister"] });
      const previousAnimals = queryClient.getQueryData<FormValues[]>([
        "adminAnimalRegister",
      ]);

      queryClient.setQueryData<FormValues[]>(
        ["adminAnimalRegister"],
        (old: any) => {
          if (!old) return [];

          return [...old, { ...data }];
        },
      );

      return { previousAnimals };
    },
    onSuccess: useCallback(() => {
      queryClient.invalidateQueries({ queryKey: ["adminAnimalRegister"] });
      toaster.success("Animal has been registered  successfully");
      localStorage.removeItem(FORM_STORAGE_KEY);
      navigate("/admin?type=Sila%20Owned%20Animals");
    }, [queryClient]),
    onError: useCallback(
      (error: Error, context: any) => {
        if (error instanceof AxiosError) {
          toaster.error(`${error.response?.data.message}`);
        }
        toaster.error("Failed to register new Animal");
        queryClient.setQueryData(
          ["adminAnimalRegister"],
          context?.previousAnimals,
        );
      },
      [queryClient],
    ),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["adminAnimalRegister"] });
    },
  });
  const [img, setImg] = useState<File[]>([]);

  const methods = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
  });

  const bullType = methods.getValues("bullType");

  const steps = [
    { title: "Basic Information", component: BasicInfoForm },
    {
      title: "Physical Attributes",
      component: PhysicalAttributesForm,
    },
    { title: "Genetic Information", component: GeneticInfoForm },
    {
      title: "Parental Information",
      component: ParentalInfoForm,
    },
    {
      title: "Grand parental Information",
      component: GrandParentalInfoForm,
    },
    {
      title: "Great Grand parental Information",
      component: GreatGrandparentalInfoForm,
    },
    { title: "Status Information", component: StatusInfoForm },

    // Conditional steps:
    ...(bullType === "Dairy Bull"
      ? [{ title: "Milk Clarifide", component: MilkProperties }]
      : // Add alternative steps here if not "Beef Bull"
        // Example:
        [
          { title: "Genomic Data", component: GeneticBreedingValuesForm },
          { title: "Body Measurements", component: BodyMeasurementsForm },
        ]),
    {
      title: "Photo and Video Information",
      component: AnimalMediaInfo,
    },
    { title: "Summary", component: FormSummary },
  ];

  useEffect(() => {
    setProgress(((currentStep + 1) / steps.length) * 100);
  }, [currentStep, steps.length]);

  const handleNext = async () => {
    console.log("birthdate", methods.getValues("birthDate"));

    if (currentStep < steps.length - 1 && !isAnimating) {
      // Validate current step before proceeding
      const isValid = await methods.trigger(
        Object.keys(methods.getValues()) as any,
      );

      if (isValid) {
        animateTransition("next");
      } else {
        toast({
          title: "Validation Error",
          description: "Please fix the errors before proceeding.",
          duration: 1000,
        });
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0 && !isAnimating) {
      animateTransition("prev");
    }
  };

  const animateTransition = (direction: "next" | "prev") => {
    if (!formRef.current || isAnimating) return;

    setIsAnimating(true);
    setAnimationDirection(direction);

    // Step 1: Animate out
    setTimeout(() => {
      // Step 2: Update step
      setCurrentStep((prev) => (direction === "next" ? prev + 1 : prev - 1));

      // Step 3: Reset animation direction for the next
      // component to animate in
      setAnimationDirection(null);

      // Step 4: Allow new animations after a delay
      setTimeout(() => {
        setIsAnimating(false);
      }, 300);
    }, 300);
  };

  const CurrentStepComponent = steps[currentStep].component;

  // Determine animation classes
  const getAnimationClass = () => {
    if (animationDirection === "next") return "animate-slide-out-left";
    if (animationDirection === "prev") return "animate-slide-out-right";

    return "animate-slide-in";
  };

  // const onSubmit: SubmitHandler<FormValues> = async () => {
  //   try {
  //     if (!img) {
  //       toaster.error("No image provided.");

  //       return;
  //     }

  //     img.map(async (image) => {
  //       const storageRef = ref(
  //         storage,
  //         `images/silaAnimals/${Date.now()}-${image.name}`,
  //       );
  //       const uploadTask = uploadBytesResumable(storageRef, image, metadata);

  //       uploadTask.on(
  //         "state_changed",
  //         (snapshot) => {
  //           // Progress handling
  //           const progress =
  //             (snapshot.bytesTransferred / snapshot.totalBytes) * 100;

  //           switch (snapshot.state) {
  //             case "paused":
  //               console.log("Upload is paused");
  //               break;
  //             case "running":
  //               console.log("Upload is running");
  //               toaster.info("Images Being Uploaded: " + progress);
  //               break;
  //           }
  //         },
  //         (error) => {
  //           switch (error.code) {
  //             case "storage/unauthorized":
  //               // User doesn't have permission to access the
  //               // object
  //               break;
  //             case "storage/canceled":
  //               // User canceled the upload
  //               break;

  //             case "storage/unknown":
  //               // Unknown error occurred, inspect
  //               // error.serverResponse
  //               break;
  //           }
  //         },
  //         async () => {
  //           await getDownloadURL(uploadTask.snapshot.ref).then(
  //             (downloadURL) => {
  //               setImgUrls((prev) => [...prev, downloadURL]);
  //             },
  //           );

  //           const formData = methods.getValues();
  //           // console.log("formData:", formData);

  //           toaster.promise(
  //             mutateAsync({
  //               ...formData,
  //               imageUrls: imgUrls,
  //               videos: [...videos],
  //             }),
  //             {
  //               success: "Animal Registered Successfully",
  //               position: "top-right",
  //             },
  //           );
  //         },
  //       );
  //     });
  //   } catch (e) {
  //     throw e;
  //   }
  // };

  // the real working
  // const uploadImageToFirebase = async (file: File, type: string, animalDgr:
  // string) => { if (!file || !storage) return null  const storagePath =
  // `images/silaAnimals/${type}/${Date.now()}-${file.name}` const storageRef =
  // ref(storage, storagePath)  // Optional metadata if you need it const metadata =
  // { contentType: file.type, customMetadata: { animalType: type, userId:
  // animalDgr, // Include userId if available } as { [key: string]: string }, }
  // const uploadTask = uploadBytesResumable(storageRef, file, metadata)  return new
  // Promise((resolve, reject) => { uploadTask.on( "state_changed", (snapshot) => {
  // // Progress handling const progress = (snapshot.bytesTransferred /
  // snapshot.totalBytes) * 100 setUploadProgress((prev) => ({ ...prev, [type]:
  // progress })) console.log(`Upload of ${type} is ${progress.toFixed(2)}% done`)
  // }, (error) => { console.error(`Error uploading ${type} image:`, error)
  // setUploadProgress((prev) => ({ ...prev, [type]: -1 })) // Indicate // error
  // reject(error) }, async () => { // Upload complete, get the download URL try {
  // const downloadURL = await getDownloadURL(uploadTask.snapshot.ref)
  // setUploadProgress((prev) => ({ ...prev, [type]: 100 })) // Mark // as //
  // complete resolve(downloadURL) } catch (error) { console.error(`Error getting
  // download URL for ${type}:`, error) reject(error) } } ) }) }

  // const onSubmit: SubmitHandler<FormValues> = async () => {
  // 	try {
  // 		if (!img || img.length === 0) {
  // 			toaster.error("No image provided.")
  // 			return
  // 		}

  // 		// 1. Upload all images and collect URLs
  // 		const uploadPromises = img.map((image) => {
  // 			const storageRef = ref(storage,
  // `images/silaAnimals/${Date.now()}-${image.name}`) const uploadTask =
  // uploadBytesResumable(storageRef, image, metadata)

  // 			return new Promise<string>((resolve, reject) => {
  // 				uploadTask.on(
  // 					"state_changed",
  // 					(snapshot) => {
  // 						// Progress handling (optional)
  // 						const progress = (snapshot.bytesTransferred / snapshot.totalBytes)
  // * 100 if (snapshot.state === "running") { toaster.info("Images Being Uploaded:
  // " + progress) } }, (error) => reject(error), async () => { const downloadURL =
  // await getDownloadURL(uploadTask.snapshot.ref) resolve(downloadURL) } ) })

  // 			// let progress = 0;
  // 			// return new Promise<string>((resolve, reject) => {
  // 			//   toaster.promise(
  // 			//     new Promise<void>((innerResolve, innerReject) => {
  // 			//       uploadTask.on(
  // 			//         "state_changed",
  // 			//         (snapshot) => {
  // 			//           progress =
  // 			//             (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
  // 			//           console.log("progress", progress);
  // 			//           setpercent(
  // 			//             (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
  // 			//           );
  // 			//         },
  // 			//         (error) => {
  // 			//           innerReject(error);
  // 			//           reject(error);
  // 			//         },
  // 			//         async () => {
  // 			//           const downloadURL = await getDownloadURL(
  // 			//             uploadTask.snapshot.ref,
  // 			//           );
  // 			//           resolve(downloadURL);
  // 			//           innerResolve();
  // 			//         },
  // 			//       );
  // 			//     }),
  // 			//     {
  // 			//       loading: "Uploading image..." + percent + "%",
  // 			//       success: "Image uploaded successfully!",
  // 			//       error: "Failed to upload image.",
  // 			//     },
  // 			//   );
  // 			// });
  // 		})

  // 		// 2. Wait for all uploads to finish
  // 		const urls = await Promise.all(uploadPromises)

  // 		// 3. Register animal ONCE with all image URLs
  // 		const formData = methods.getValues()
  // 		console.log("formData:", formData)
  // 		toaster.promise(
  // 			mutateAsync({
  // 				...formData,
  // 				imageUrls: urls,
  // 				videos: [...videos],
  // 			}),
  // 			{
  // 				success: "Animal Registered Successfully",
  // 				position: "top-right",
  // 			}
  // 		)
  // 		setImgUrls(urls) // if you still need to store them in state
  // 	} catch (e) {
  // 		throw e
  // 	}
  // }

  // const onSubmitWithLineageUploads: SubmitHandler<FormValues> = async () => {
  // 	try {
  // 		const formData = methods.getValues()
  // 		// Upload all lineage images and collect URLs
  // 		const lineageUploads = [
  // 			{ file: damImageFile, type: "dam", field: "damImageUrl", dgr:
  // formData.damDGR }, { file: sireImageFile, type: "sire", field: "sireImageUrl",
  // dgr: formData.sireDGR }, { file: paternalGrandfatherImageFile, type:
  // "paternalGrandfather", field: "paternalGrandSireImageUrl", dgr:
  // formData.paternalGrandSireDGR, }, { file: paternalGrandmotherImageFile, type:
  // "paternalGrandmother", field: "paternalGrandDamImageUrl", dgr:
  // formData.paternalGrandDamDGR, }, { file: maternalGrandfatherImageFile, type:
  // "maternalGrandfather", field: "maternalGrandSireImageUrl", dgr:
  // formData.maternalGrandSireDGR, }, { file: maternalGrandmotherImageFile, type:
  // "maternalGrandmother", field: "maternalGrandDamImageUrl", dgr:
  // formData.maternalGrandDamDGR, }, // 8 great-grandparents { file:
  // paternalGrandSireGrandfatherImageFile, type: "paternalGrandSireGrandfather",
  // field: "paternalGrandSireGrandSireImageUrl", dgr:
  // formData.paternalGrandSireGrandSireDGR, }, { file:
  // paternalGrandSireGrandmotherImageFile, type: "paternalGrandSireGrandmother",
  // field: "paternalGrandSireGrandDamImageUrl", dgr:
  // formData.paternalGrandSireGrandDamDGR, }, { file:
  // paternalGrandDamGrandfatherImageFile, type: "paternalGrandDamGrandfather",
  // field: "paternalGrandDamGrandSireImageUrl", dgr:
  // formData.paternalGrandDamGrandSireDGR, }, { file:
  // paternalGrandDamGrandmotherImageFile, type: "paternalGrandDamGrandmother",
  // field: "paternalGrandDamGrandDamImageUrl", dgr:
  // formData.paternalGrandDamGrandDamDGR, }, { file:
  // maternalGrandSireGrandfatherImageFile, type: "maternalGrandSireGrandfather",
  // field: "maternalGrandSireGrandSireImageUrl", dgr:
  // formData.maternalGrandSireGrandSireDGR, }, { file:
  // maternalGrandSireGrandmotherImageFile, type: "maternalGrandSireGrandmother",
  // field: "maternalGrandSireGrandDamImageUrl", dgr:
  // formData.maternalGrandSireGrandDamDGR, }, { file:
  // maternalGrandDamGrandfatherImageFile, type: "maternalGrandDamGrandfather",
  // field: "maternalGrandDamGrandSireImageUrl", dgr:
  // formData.maternalGrandDamGrandSireDGR, }, { file:
  // maternalGrandDamGrandmotherImageFile, type: "maternalGrandDamGrandmother",
  // field: "maternalGrandDamGrandDamImageUrl", dgr:
  // formData.maternalGrandDamGrandDamDGR, }, ] const urlMap: Record<string, string
  // | null> = {} for (const item of lineageUploads) { if (item.file && typeof
  // item.file !== "string" && item.file instanceof File) { urlMap[item.field] =
  // (await uploadImageToFirebase(item.file, item.type, item.dgr)) as | string |
  // null } } // Merge URLs into formData const newFormData = { ...formData,
  // ...urlMap }

  // 		console.log("newFormData:", newFormData)
  // 		// Submit with new formData
  // 		await toaster.promise(
  // 			mutateAsync({
  // 				...newFormData,
  // 				imageUrls: imgUrls, // or update as needed
  // 				videos: [...videos],
  // 			}),
  // 			{
  // 				success: "Animal Registered Successfully",
  // 				position: "top-right",
  // 			}
  // 		)
  // 	} catch (e) {
  // 		throw e
  // 	}
  // }

  // New submit handler that uploads all lineage images and saves their URLs in
  // formData

  // const onSubmitWithLineageUploads: SubmitHandler<FormValues> = async () => {
  //   try {
  //     const formData = methods.getValues();
  //
  //     // Upload all lineage images and collect URLs
  //     const lineageUploads = [
  //       {
  //         file: damImageFile,
  //         type: "dam",
  //         field: "damImageUrl",
  //         dgr: formData.damDGR,
  //       },
  //       {
  //         file: sireImageFile,
  //         type: "sire",
  //         field: "sireImageUrl",
  //         dgr: formData.sireDGR,
  //       },
  //       {
  //         file: paternalGrandfatherImageFile,
  //         type: "paternalGrandfather",
  //         field: "paternalGrandSireImageUrl",
  //         dgr: formData.paternalGrandSireDGR,
  //       },
  //       {
  //         file: paternalGrandmotherImageFile,
  //         type: "paternalGrandmother",
  //         field: "paternalGrandDamImageUrl",
  //         dgr: formData.paternalGrandDamDGR,
  //       },
  //       {
  //         file: maternalGrandfatherImageFile,
  //         type: "maternalGrandfather",
  //         field: "maternalGrandSireImageUrl",
  //         dgr: formData.maternalGrandSireDGR,
  //       },
  //       {
  //         file: maternalGrandmotherImageFile,
  //         type: "maternalGrandmother",
  //         field: "maternalGrandDamImageUrl",
  //         dgr: formData.maternalGrandDamDGR,
  //       },
  //       // 8 great-grandparents
  //       {
  //         file: paternalGrandSireGrandfatherImageFile,
  //         type: "paternalGrandSireGrandfather",
  //         field: "paternalGrandSireGrandSireImageUrl",
  //         dgr: formData.paternalGrandSireGrandSireDGR,
  //       },
  //       {
  //         file: paternalGrandSireGrandmotherImageFile,
  //         type: "paternalGrandSireGrandmother",
  //         field: "paternalGrandSireGrandDamImageUrl",
  //         dgr: formData.paternalGrandSireGrandDamDGR,
  //       },
  //       {
  //         file: paternalGrandDamGrandfatherImageFile,
  //         type: "paternalGrandDamGrandfather",
  //         field: "paternalGrandDamGrandSireImageUrl",
  //         dgr: formData.paternalGrandDamGrandSireDGR,
  //       },
  //       {
  //         file: paternalGrandDamGrandmotherImageFile,
  //         type: "paternalGrandDamGrandmother",
  //         field: "paternalGrandDamGrandDamImageUrl",
  //         dgr: formData.paternalGrandDamGrandDamDGR,
  //       },
  //       {
  //         file: maternalGrandSireGrandfatherImageFile,
  //         type: "maternalGrandSireGrandfather",
  //         field: "maternalGrandSireGrandSireImageUrl",
  //         dgr: formData.maternalGrandSireGrandSireDGR,
  //       },
  //       {
  //         file: maternalGrandSireGrandmotherImageFile,
  //         type: "maternalGrandSireGrandmother",
  //         field: "maternalGrandSireGrandDamImageUrl",
  //         dgr: formData.maternalGrandSireGrandDamDGR,
  //       },
  //       {
  //         file: maternalGrandDamGrandfatherImageFile,
  //         type: "maternalGrandDamGrandfather",
  //         field: "maternalGrandDamGrandSireImageUrl",
  //         dgr: formData.maternalGrandDamGrandSireDGR,
  //       },
  //       {
  //         file: maternalGrandDamGrandmotherImageFile,
  //         type: "maternalGrandDamGrandmother",
  //         field: "maternalGrandDamGrandDamImageUrl",
  //         dgr: formData.maternalGrandDamGrandDamDGR,
  //       },
  //     ];
  //     const urlMap: Record<string, string | null> = {};
  //     for (const item of lineageUploads) {
  //       if (item.file && item.file instanceof File) {
  //         console.log("item type", typeof item.file);
  //         urlMap[item.field] = (await uploadImageToFirebase(
  //           item.file,
  //           item.type,
  //           item.dgr,
  //         )) as string | null;
  //       }
  //     }
  //
  //     // // upload the animal's image
  //     // const mainImageUploadPromises =
  //     //   img?.map((file) =>
  //     //     uploadImageToFirebase(file, "main-animal", methods.getValues("dgr")),
  //     //   ) || [];
  //     //
  //     // const uploadedImageUrls = (await Promise.all(
  //     //   mainImageUploadPromises,
  //     // )) as string[];
  //     //
  //     // console.log("Here are the URLs:", uploadedImageUrls);
  //     // // Merge URLs into formData
  //     // const newFormData = { ...formData, ...urlMap };
  //     //
  //     // // Submit with new formData
  //     // toaster.promise(
  //     //   mutateAsync({
  //     //     ...newFormData,
  //     //     imageUrls: [...uploadedImageUrls], // or update as needed
  //     //     videos: [...videos],
  //     //   }),
  //     //   {
  //     //     success: "Animal Registered Successfully",
  //     //     position: "top-right",
  //     //   },
  //     // );
  //   } catch (e) {
  //     throw e;
  //   }
  // };

  const formData = methods.getValues();

  const lineageUploads = [
    {
      file: damImageFile,
      type: "dam",
      field: "damImageUrl",
      dgr: formData.damDGR,
      displayName: "Dam",
    },
    {
      file: sireImageFile,
      type: "sire",
      field: "sireImageUrl",
      dgr: formData.sireDGR,
      displayName: "Sire",
    },
    {
      file: paternalGrandfatherImageFile,
      type: "paternalGrandfather",
      field: "paternalGrandSireImageUrl",
      dgr: formData.paternalGrandSireDGR,
      displayName: "Paternal Grand Sire",
    },
    {
      file: paternalGrandmotherImageFile,
      type: "paternalGrandmother",
      field: "paternalGrandDamImageUrl",
      dgr: formData.paternalGrandDamDGR,
      displayName: "Paternal Grand Dam",
    },
    {
      file: maternalGrandfatherImageFile,
      type: "maternalGrandfather",
      field: "maternalGrandSireImageUrl",
      dgr: formData.maternalGrandSireDGR,
      displayName: "Maternal Grand Sire",
    },
    {
      file: maternalGrandmotherImageFile,
      type: "maternalGrandmother",
      field: "maternalGrandDamImageUrl",
      dgr: formData.maternalGrandDamDGR,
      displayName: "Maternal Grand Dam",
    },
    // 8 great-grandparents
    {
      file: paternalGrandSireGrandfatherImageFile,
      type: "paternalGrandSireGrandfather",
      field: "paternalGrandSireGrandSireImageUrl",
      dgr: formData.paternalGrandSireGrandSireDGR,
      displayName: "Paternal Grand Sire's Sire",
    },
    {
      file: paternalGrandSireGrandmotherImageFile,
      type: "paternalGrandSireGrandmother",
      field: "paternalGrandSireGrandDamImageUrl",
      dgr: formData.paternalGrandSireGrandDamDGR,
      displayName: "Paternal Grand Sire's Dam",
    },
    {
      file: paternalGrandDamGrandfatherImageFile,
      type: "paternalGrandDamGrandfather",
      field: "paternalGrandDamGrandSireImageUrl",
      dgr: formData.paternalGrandDamGrandSireDGR,
      displayName: "Paternal Grand Dam's Sire",
    },
    {
      file: paternalGrandDamGrandmotherImageFile,
      type: "paternalGrandDamGrandmother",
      field: "paternalGrandDamGrandDamImageUrl",
      dgr: formData.paternalGrandDamGrandDamDGR,
      displayName: "Paternal Grand Dam's Dam",
    },
    {
      file: maternalGrandSireGrandfatherImageFile,
      type: "maternalGrandSireGrandfather",
      field: "maternalGrandSireGrandSireImageUrl",
      dgr: formData.maternalGrandSireGrandSireDGR,
      displayName: "Maternal Grand Sire's Sire",
    },
    {
      file: maternalGrandSireGrandmotherImageFile,
      type: "maternalGrandSireGrandmother",
      field: "maternalGrandSireGrandDamImageUrl",
      dgr: formData.maternalGrandSireGrandDamDGR,
      displayName: "Maternal Grand Sire's Dam",
    },
    {
      file: maternalGrandDamGrandfatherImageFile,
      type: "maternalGrandDamGrandfather",
      field: "maternalGrandDamGrandSireImageUrl",
      dgr: formData.maternalGrandDamGrandSireDGR,
      displayName: "Maternal Grand Dam's Sire",
    },
    {
      file: maternalGrandDamGrandmotherImageFile,
      type: "maternalGrandDamGrandmother",
      field: "maternalGrandDamGrandDamImageUrl",
      dgr: formData.maternalGrandDamGrandDamDGR,
      displayName: "Maternal Grand Dam's Dam",
    },
  ];

  // const onSubmitWithLineageUploads: SubmitHandler<FormValues> = async () => {
  // 	try {
  // 		const formData = methods.getValues()
  // 		// Upload all lineage images and collect URLs
  // 		// const lineageUploads = [
  // 		// 	{
  // 		// 		file: damImageFile,
  // 		// 		type: "dam",
  // 		// 		field: "damImageUrl",
  // 		// 		dgr: formData.damDGR,
  // 		// 	},
  // 		// 	{
  // 		// 		file: sireImageFile,
  // 		// 		type: "sire",
  // 		// 		field: "sireImageUrl",
  // 		// 		dgr: formData.sireDGR,
  // 		// 	},
  // 		// 	{
  // 		// 		file: paternalGrandfatherImageFile,
  // 		// 		type: "paternalGrandfather",
  // 		// 		field: "paternalGrandSireImageUrl",
  // 		// 		dgr: formData.paternalGrandSireDGR,
  // 		// 	},
  // 		// 	{
  // 		// 		file: paternalGrandmotherImageFile,
  // 		// 		type: "paternalGrandmother",
  // 		// 		field: "paternalGrandDamImageUrl",
  // 		// 		dgr: formData.paternalGrandDamDGR,
  // 		// 	},
  // 		// 	{
  // 		// 		file: maternalGrandfatherImageFile,
  // 		// 		type: "maternalGrandfather",
  // 		// 		field: "maternalGrandSireImageUrl",
  // 		// 		dgr: formData.maternalGrandSireDGR,
  // 		// 	},
  // 		// 	{
  // 		// 		file: maternalGrandmotherImageFile,
  // 		// 		type: "maternalGrandmother",
  // 		// 		field: "maternalGrandDamImageUrl",
  // 		// 		dgr: formData.maternalGrandDamDGR,
  // 		// 	},
  // 		// 	// 8 great-grandparents
  // 		// 	{
  // 		// 		file: paternalGrandSireGrandfatherImageFile,
  // 		// 		type: "paternalGrandSireGrandfather",
  // 		// 		field: "paternalGrandSireGrandSireImageUrl",
  // 		// 		dgr: formData.paternalGrandSireGrandSireDGR,
  // 		// 	},
  // 		// 	{
  // 		// 		file: paternalGrandSireGrandmotherImageFile,
  // 		// 		type: "paternalGrandSireGrandmother",
  // 		// 		field: "paternalGrandSireGrandDamImageUrl",
  // 		// 		dgr: formData.paternalGrandSireGrandDamDGR,
  // 		// 	},
  // 		// 	{
  // 		// 		file: paternalGrandDamGrandfatherImageFile,
  // 		// 		type: "paternalGrandDamGrandfather",
  // 		// 		field: "paternalGrandDamGrandSireImageUrl",
  // 		// 		dgr: formData.paternalGrandDamGrandSireDGR,
  // 		// 	},
  // 		// 	{
  // 		// 		file: paternalGrandDamGrandmotherImageFile,
  // 		// 		type: "paternalGrandDamGrandmother",
  // 		// 		field: "paternalGrandDamGrandDamImageUrl",
  // 		// 		dgr: formData.paternalGrandDamGrandDamDGR,
  // 		// 	},
  // 		// 	{
  // 		// 		file: maternalGrandSireGrandfatherImageFile,
  // 		// 		type: "maternalGrandSireGrandfather",
  // 		// 		field: "maternalGrandSireGrandSireImageUrl",
  // 		// 		dgr: formData.maternalGrandSireGrandSireDGR,
  // 		// 	},
  // 		// 	{
  // 		// 		file: maternalGrandSireGrandmotherImageFile,
  // 		// 		type: "maternalGrandSireGrandmother",
  // 		// 		field: "maternalGrandSireGrandDamImageUrl",
  // 		// 		dgr: formData.maternalGrandSireGrandDamDGR,
  // 		// 	},
  // 		// 	{
  // 		// 		file: maternalGrandDamGrandfatherImageFile,
  // 		// 		type: "maternalGrandDamGrandfather",
  // 		// 		field: "maternalGrandDamGrandSireImageUrl",
  // 		// 		dgr: formData.maternalGrandDamGrandSireDGR,
  // 		// 	},
  // 		// 	{
  // 		// 		file: maternalGrandDamGrandmotherImageFile,
  // 		// 		type: "maternalGrandDamGrandmother",
  // 		// 		field: "maternalGrandDamGrandDamImageUrl",
  // 		// 		dgr: formData.maternalGrandDamGrandDamDGR,
  // 		// 	},
  // 		// ]

  // 		// console.log("lineageUploads", lineageUploads)
  // 		// const urlMap: Record<string, string | undefined> = {}
  // 		// for (const item of lineageUploads) {
  // 		// 	if (item.file && item.file instanceof File) {
  // 		// 		console.log(item.field, item.file, typeof item.file, item.file
  // instanceof File)

  // 		// 		urlMap[item.field] = (await uploadImageToFirebase(item.file,
  // item.type, item.dgr)) as // 			| string // 			| undefined // 	} // }

  // 		// // upload the animal's image
  // 		// const mainImageUploadPromises =
  // 		// 	img?.map((file) => uploadImageToFirebase(file, "main-animal",
  // methods.getValues("dgr"))) || // 	[]

  // 		// const uploadedImageUrls = (await Promise.all(mainImageUploadPromises))
  // as string[]

  // 		// Merge URLs into formData
  // 		const newFormData = { ...formData, ...urlMap }
  // 		// Submit with new formData
  // 		toaster.promise(
  // 			mutateAsync({
  // 				...newFormData,
  // 				imageUrls: [...uploadedImageUrls], // or update as needed
  // 				videos: [...videos],
  // 			}),
  // 			{
  // 				success: "Animal Registered Successfully",
  // 				position: "top-right",
  // 			}
  // 		)
  // 	} catch (e) {
  // 		throw e
  // 	}
  // }

  const onSubmitWithLineageUploads: SubmitHandler<FormValues> = async () => {
    try {
      if (!img || img.length === 0) {
        toaster.error("No image provided.");
        return;
      }

      const formData = methods.getValues();

      if (!uploadRef.current) {
        toaster.error("Upload manager is not ready.");
        return;
      }
      const { animalImageUrls, lineageImageUrls } =
        await uploadRef.current.startUploads();

      // Merge URLs into formData
      const newFormData = { ...formData, ...lineageImageUrls };

      // Submit with new formData
      toaster.promise(
        mutateAsync({
          ...newFormData,
          imageUrls: [...animalImageUrls], // or update as needed
          videos: [...videos],
        }),
        {
          loading: "Sila Owned Animal is Being Registered",
          success: "Animal Registered Successfully",
          position: "top-right",
        },
      );
    } catch (e) {
      throw e;
    }
  };

  useEffect(() => {
    const saved = localStorage.getItem(FORM_STORAGE_KEY);
    if (saved) {
      methods.reset(JSON.parse(saved));
    }
  }, [methods]);

  useEffect(() => {
    const subscription = methods.watch((values) => {
      localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(values));
    });
    return () => subscription.unsubscribe();
  }, [methods]);

  return (
    <FormProvider {...methods}>
      <SequentialUploadNotifier
        ref={uploadRef}
        animalImages={img}
        lineageImages={lineageUploads}
      />
      <div className="w-full mx-auto lg:px-4 z-10">
        <div className="mb-8 px-2">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Step {currentStep + 1} of {steps.length}
            </span>
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
              {steps[currentStep].title}
            </span>
          </div>
          <Progress className="h-2" value={progress} />
        </div>

        <Card className="border border-slate-200 dark:border-slate-700 shadow-lg">
          <CardContent className="p-4 sm:py-6">
            <form onSubmit={methods.handleSubmit(onSubmitWithLineageUploads)}>
              <div
                ref={formRef}
                className={`min-h-[400px] transition-all duration-300 ${getAnimationClass()}`}
              >
                <CurrentStepComponent
                  img={img}
                  setImg={setImg}
                  setVideos={setVideos}
                  videos={videos}
                  // parents setters
                  damImageFile={damImageFile}
                  sireImageFile={sireImageFile}
                  setDamImageFile={setDamImageFile}
                  setSireImageFile={setSireImageFile}
                  // grand parents setters
                  setPaternalGrandfatherImageFile={
                    setPaternalGrandfatherImageFile
                  }
                  setPaternalGrandmotherImageFile={
                    setPaternalGrandmotherImageFile
                  }
                  setMaternalGrandfatherImageFile={
                    setMaternalGrandfatherImageFile
                  }
                  setMaternalGrandmotherImageFile={
                    setMaternalGrandmotherImageFile
                  }
                  // Great-grandparent setters
                  setPaternalGrandSireGrandfatherImageFile={
                    setPaternalGrandSireGrandfatherImageFile
                  }
                  setPaternalGrandSireGrandmotherImageFile={
                    setPaternalGrandSireGrandmotherImageFile
                  }
                  setPaternalGrandDamGrandfatherImageFile={
                    setPaternalGrandDamGrandfatherImageFile
                  }
                  setPaternalGrandDamGrandmotherImageFile={
                    setPaternalGrandDamGrandmotherImageFile
                  }
                  setMaternalGrandSireGrandfatherImageFile={
                    setMaternalGrandSireGrandfatherImageFile
                  }
                  setMaternalGrandSireGrandmotherImageFile={
                    setMaternalGrandSireGrandmotherImageFile
                  }
                  setMaternalGrandDamGrandfatherImageFile={
                    setMaternalGrandDamGrandfatherImageFile
                  }
                  setMaternalGrandDamGrandmotherImageFile={
                    setMaternalGrandDamGrandmotherImageFile
                  }
                />
              </div>

              <div className="flex justify-between mt-8">
                <Button
                  className="flex items-center gap-2"
                  disabled={currentStep === 0 || isAnimating}
                  type="button"
                  variant="outline"
                  onClick={handlePrevious}
                >
                  <ChevronLeft className="h-4 w-4" />
                  {!isMobile && "Previous"}
                </Button>

                <div className="flex gap-2">
                  {currentStep === steps.length - 1 ? (
                    <Button
                      type="submit"
                      // onClick={ handleSubmit }
                      className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
                    >
                      <Save className="h-4 w-4" />
                      {!isMobile ? "Submit Registration" : "Submit"}
                    </Button>
                  ) : (
                    <Button
                      className="flex items-center gap-2 z-10"
                      disabled={isAnimating}
                      type="button"
                      onClick={handleNext}
                    >
                      {!isMobile && "Next"}
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </form>
          </CardContent>
        </Card>

        <div className="mt-8 flex justify-center">
          <div className="flex flex-wrap justify-center gap-2">
            {steps.map((_, index) => (
              <button
                key={index}
                aria-label={`Go to step ${index + 1}`}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentStep
                    ? "bg-[#008c8fff] scale-125"
                    : index < currentStep
                      ? "bg-green-500"
                      : "bg-slate-300 dark:bg-slate-600"
                }`}
                disabled={isAnimating}
                type="button"
                onClick={() => !isAnimating && setCurrentStep(index)}
              />
            ))}
          </div>
        </div>
      </div>
    </FormProvider>
  );
}
